"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Globe, Info, LinkIcon, PlusIcon, Settings, Share2 } from "lucide-react";

import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import availableLanguages from "@/i18n/availableLanguages";
import PATHS from "@/routes/paths.ts";
import { Separator } from "@/components/ui/separator";
import { getLanguageFromLocale } from "@/features/profile/utils/profileUtils.ts";
import envs from "@/envs.ts";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { CoachMarker } from "@/components/CoachMarker.tsx";
import { useProfileEditDialogStore } from "@/features/profile/tutor/store/useProfileEditDialogStore";

// Shake animation variants
const shakeAnimation = {
  shake: {
    x: [0, -1.5, 1.5, -2, 2, 0],
    transition: { duration: 0.4, ease: "easeInOut" },
  },
};

export default function SettingsCard() {
  const { settingsData: data, profileData } = useProfileDataStore();
  const redirect = useNavigate();
  const { open: openEditDialog } = useProfileEditDialogStore();
  const [copied, setCopied] = useState(false); // State for copy button feedback
  const { t } = useTranslation(["profile", "common", "help"], {
    lng: getLanguageFromLocale(profileData?.header_section.locale_code),
  });

  if (!data) return null;

  const handleLanguageChange = (locale: string) => {
    redirect(`${PATHS.TUTOR.PUBLIC_PROFILE}/${locale}`);
  };

  // openEditDialog("settings") handles dialog interactions now

  const profileLink = `${envs.VITE_SITE_URL}/profile/tutor/${data.visibility.profile_url_slug}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(profileLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareProfileLink = () => {
    if (navigator.share) {
      navigator
        .share({
          url: profileLink,
        })
        .catch((error) => console.log("Error sharing:", error));
    } else {
      copyToClipboard();
    }
  };

  // Get the main language details
  const mainLanguage = availableLanguages.find(
    (lang) => lang.language.toUpperCase() === data.main_profile.main_profile_locale,
  );

  // Get other languages
  const otherLanguages = data.main_profile.profiles_locales
    .filter((locale: string) => locale !== data.main_profile.main_profile_locale)
    .map((locale: string) =>
      availableLanguages.find((lang) => lang.language.toUpperCase() === locale),
    )
    .filter(Boolean);

  const orderedLanguages = [mainLanguage, ...otherLanguages].filter(Boolean);

  return (
    <CoachMarker
      description={t("help:coachMarkers.profileSettings.description")}
      id="profile-settings"
      placement="left"
      title={t("help:coachMarkers.profileSettings.title")}
    >
      <Card className="bg-side-accent/50 relative h-fit w-full rounded-2xl border-l-4 border-none border-l-indigo-400/50 shadow-2xl backdrop-blur-xl sm:min-w-[23rem] xl:w-fit xl:max-w-[24rem] 2xl:max-w-[35rem]">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-gradient-to-br from-indigo-400 to-purple-600" />
          <div className="absolute -bottom-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-tr from-purple-400 to-indigo-500" />
        </div>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="bg-foreground/50 hover:bg-foreground/70 absolute top-2 right-2 z-50 rounded-xl backdrop-blur-sm"
                size="icon"
                variant="ghostSecondary"
                onClick={() => openEditDialog("settings")}
              >
                <Settings className="text-muted-foreground h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{t("common:settings")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <CardContent className="relative flex max-w-4xl flex-col justify-start gap-0 space-y-6">
          <div className="w-full">
            <CardHeader className="flex flex-row justify-between ps-0 pb-3">
              <CardTitle className="text-accent-foreground flex items-center gap-3 text-xl font-bold">
                <div className="rounded-xl p-2">
                  <Globe className="h-5 w-5 text-indigo-400" />
                </div>
                {t("profile:settingsSection.profileLanguage")}
              </CardTitle>
            </CardHeader>
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-2"
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5 }}
            >
              <Tabs
                className="w-full"
                defaultValue={profileData?.header_section?.locale_code}
                onValueChange={handleLanguageChange}
              >
                <TabsList className="flex w-full gap-2 rounded-xl bg-transparent backdrop-blur-sm">
                  {orderedLanguages.map((lang, index) => (
                    <motion.div
                      key={`${lang.language}-${index}`}
                      animate={{ opacity: 1 }}
                      className="w-full"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <TabsTrigger
                        className={`flex w-full items-center gap-2 rounded-xl data-[state=active]:bg-indigo-500/10 data-[state=active]:text-indigo-300`}
                        value={lang.language.toUpperCase()}
                      >
                        <img
                          alt={`${lang.name} flag`}
                          className="h-6 w-8 rounded object-cover"
                          src={lang.flagImg || "/placeholder.svg"}
                        />
                      </TabsTrigger>
                    </motion.div>
                  ))}
                </TabsList>
              </Tabs>
              {availableLanguages.length !== orderedLanguages.length && (
                <CoachMarker
                  description={t("help:coachMarkers.profileNewLanguage.description")}
                  id="profile-new-language"
                  placement="left"
                  title={t("help:coachMarkers.profileNewLanguage.title")}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="rounded-xl bg-indigo-400/5 p-2 text-indigo-300 hover:bg-indigo-500/15"
                          size="icon"
                          variant="ghostSecondary"
                          onClick={() => redirect(PATHS.TUTOR.CREATE_PROFILE_NL)}
                        >
                          <PlusIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>{t("common:addNewLanguage")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CoachMarker>
              )}
            </motion.div>
          </div>

          <Separator className="bg-indigo-200/20" />

          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="box-border w-full space-y-2"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-accent-foreground flex items-center gap-2 text-lg font-semibold">
                <div className="rounded-xl p-2">
                  <LinkIcon className="h-4 w-4 text-indigo-400" />
                </div>
                {t("profile:settingsSection.publicProfileAndUrl")}
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="text-muted-foreground h-4 w-4 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-foreground/95 backdrop-blur-xl" side="left">
                    <p>{t("profile:settingsSection.publicProfileAndUrlTooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div
              className={
                "rounded-2xl bg-transparent px-2 py-2 backdrop-blur-sm hover:bg-indigo-400/2"
              }
            >
              <ScrollArea className={"h-fit w-full max-w-[80vw] py-2"}>
                <p className="text-muted-foreground text-sm text-nowrap break-all">
                  {`${envs.VITE_SITE_URL}/profile/tutor/${data.visibility.profile_url_slug}`}
                </p>
                <ScrollBar orientation={"horizontal"} />
              </ScrollArea>
            </div>
            {data.visibility.enable_public_profile && data.visibility.profile_url_slug && (
              <div className="mt-4 flex w-full gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="flex-1 rounded-xl bg-indigo-400/5 text-indigo-300 hover:bg-indigo-500/15"
                        onClick={copyToClipboard}
                      >
                        <motion.div
                          key={copied ? "check-btn" : "copy-btn"}
                          animate={copied ? "shake" : ""}
                          className="mr-2 flex items-center"
                          transition={{ type: "spring", stiffness: 300, damping: 15 }}
                          variants={shakeAnimation}
                        >
                          {copied ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </motion.div>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{t("common:copy")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        className="flex-1 rounded-xl bg-indigo-500/5 text-indigo-300 hover:bg-indigo-500/15"
                        onClick={shareProfileLink}
                      >
                        <Share2 className="mr-2 h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>{t("common:share")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </motion.div>
        </CardContent>
      </Card>
    </CoachMarker>
  );
}
